/**
 * Variant carry-over
 *
 * Lets links between sibling products (e.g. the "Individual" / "Set of 4"
 * buttons on a product page) preserve the currently selected variant.
 *
 * How it works:
 *   1. On any product page, when the user clicks an <a> that points to a
 *      different /products/... URL, we append the current option values
 *      (Clay, Glaze, ...) as query params with an "opt_" prefix.
 *   2. On product page load, if those params are present, we find the
 *      matching combined-swatch label and click it (or fall back to
 *      dispatching change events on the option <select>s). This drives the
 *      existing variant-picker / combined-swatch-picker as if the user had
 *      selected that variant manually, including the URL update to ?variant=ID.
 *
 * Using a custom "opt_" prefix avoids colliding with Shopify's reserved
 * option1/option2/Clay/Glaze query params (which behave differently across
 * products and may be stripped by the platform).
 */

(() => {
  const PARAM_PREFIX = 'opt_';

  /**
   * Read currently-selected option values from the main product form.
   * @returns {Record<string, string> | null}
   */
  function getCurrentOptions() {
    // Look at every option input on the page. Selects hold the current value;
    // for radio groups (swatches) the checked one wins.
    /** @type {Record<string, string>} */
    const options = {};
    const inputs = document.querySelectorAll(
      'select[name^="options["], input[name^="options["]:checked'
    );
    for (const el of inputs) {
      if (!(el instanceof HTMLSelectElement) && !(el instanceof HTMLInputElement)) continue;
      const match = el.name.match(/^options\[(.+)\]$/);
      if (!match) continue;
      const name = match[1];
      const value = el.value;
      if (!name || !value) continue;
      // First write wins so we don't overwrite a select with an unrelated radio.
      if (!(name in options)) options[name] = value;
    }
    return Object.keys(options).length ? options : null;
  }

  /**
   * Rewrite a single <a> href to carry the current option values.
   * @param {HTMLAnchorElement} link
   */
  function decorateProductLink(link) {
    try {
      const href = link.getAttribute('href');
      if (!href) return;
      // Skip non-product links and anchors/mailto/etc.
      if (!/\/products\//.test(href)) return;

      const url = new URL(href, window.location.origin);
      // Only same-origin product pages.
      if (url.origin !== window.location.origin) return;
      // If the link already targets a specific variant or already carries
      // carry-over params, leave it alone.
      if (url.searchParams.has('variant')) return;
      let alreadyCarried = false;
      for (const key of url.searchParams.keys()) {
        if (key.startsWith(PARAM_PREFIX)) {
          alreadyCarried = true;
          break;
        }
      }
      if (alreadyCarried) return;

      const opts = getCurrentOptions();
      if (!opts) return;

      for (const [name, val] of Object.entries(opts)) {
        url.searchParams.set(PARAM_PREFIX + name, val);
      }

      // Preserve relative hrefs to avoid changing how the link renders.
      const isRelative = !/^https?:\/\//i.test(href);
      link.setAttribute(
        'href',
        isRelative ? url.pathname + url.search + url.hash : url.toString()
      );
    } catch (_) {
      // No-op: malformed URLs etc.
    }
  }

  /**
   * Intercept clicks (and middle-click / cmd-click) on product links so we
   * can decorate them just-in-time with the latest selection.
   * @param {Event} event
   */
  function handlePointerDown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = /** @type {HTMLAnchorElement | null} */ (
      target.closest('a[href*="/products/"]')
    );
    if (!link) return;
    decorateProductLink(link);
  }

  /**
   * Apply carry-over params on the destination page.
   */
  function applyCarryoverFromUrl() {
    const params = new URLSearchParams(window.location.search);

    /** @type {Array<[string, string]>} */
    const optionEntries = [];
    for (const [key, val] of params) {
      if (key.startsWith(PARAM_PREFIX) && val) {
        optionEntries.push([key.slice(PARAM_PREFIX.length), val]);
      }
    }
    if (!optionEntries.length) return;

    // If the URL already targets a specific variant, that wins. Just strip the
    // carry-over params so we don't keep them around forever.
    if (params.get('variant')) {
      cleanUrl(params);
      return;
    }

    // Wait until variant picker / combined-swatch-picker have hydrated.
    const tryApply = () => {
      if (applySelectionFromOptions(optionEntries)) {
        cleanUrl(new URLSearchParams(window.location.search));
        return true;
      }
      return false;
    };

    if (tryApply()) return;
    // Retry briefly to allow custom elements to upgrade.
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (tryApply() || attempts >= 20) clearInterval(timer);
    }, 50);
  }

  /**
   * Try to apply the desired option values to the page.
   * Returns true once a matching selection has been made.
   * @param {Array<[string, string]>} optionEntries
   */
  function applySelectionFromOptions(optionEntries) {
    // 1) Combined-swatch picker (Clay + Glaze): pick the matching label so
    //    the swatch UI, hidden selects and URL all update consistently.
    /** @type {Record<string, string>} */
    const wanted = {};
    for (const [name, val] of optionEntries) wanted[name.toLowerCase()] = val;

    const labels = document.querySelectorAll('.combined-swatch-label');
    if (labels.length) {
      /** @type {HTMLElement | null} */
      let match = null;
      for (const label of labels) {
        if (!(label instanceof HTMLElement)) continue;
        const clay = label.dataset.clay;
        const glaze = label.dataset.glaze;
        if (
          clay &&
          glaze &&
          wanted.clay &&
          wanted.glaze &&
          clay.toLowerCase() === wanted.clay.toLowerCase() &&
          glaze.toLowerCase() === wanted.glaze.toLowerCase()
        ) {
          match = label;
          break;
        }
      }
      if (match) {
        match.click();
        return true;
      }
      // Combined picker is present but we can't match yet (maybe still hydrating).
      return false;
    }

    // 2) Fallback: dispatch change events on the option <select>s for
    //    products that use a standard variant picker without combined swatches.
    let changed = false;
    for (const [name, val] of optionEntries) {
      const select = /** @type {HTMLSelectElement | null} */ (
        document.querySelector(`select[name="options[${cssEscape(name)}]"]`)
      );
      if (select) {
        const option = Array.from(select.options).find(
          (o) => o.value.toLowerCase() === val.toLowerCase()
        );
        if (option && select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          changed = true;
        }
        continue;
      }
      const radios = document.querySelectorAll(
        `input[type="radio"][name="options[${cssEscape(name)}]"]`
      );
      for (const r of radios) {
        if (!(r instanceof HTMLInputElement)) continue;
        if (r.value.toLowerCase() === val.toLowerCase() && !r.checked) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
          changed = true;
          break;
        }
      }
    }
    return changed;
  }

  /**
   * Strip carry-over params from the URL without reloading.
   * @param {URLSearchParams} params
   */
  function cleanUrl(params) {
    let mutated = false;
    for (const key of Array.from(params.keys())) {
      if (key.startsWith(PARAM_PREFIX)) {
        params.delete(key);
        mutated = true;
      }
    }
    if (!mutated) return;
    const search = params.toString();
    const newUrl =
      window.location.pathname +
      (search ? '?' + search : '') +
      window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }

  /**
   * @param {string} value
   */
  function cssEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
    return value.replace(/["\\]/g, '\\$&');
  }

  // Use capture so we run before the browser navigates.
  document.addEventListener('pointerdown', handlePointerDown, true);
  // Cover keyboard activation too (Enter on a focused link).
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const active = document.activeElement;
      if (active instanceof HTMLAnchorElement && /\/products\//.test(active.href)) {
        decorateProductLink(active);
      }
    },
    true
  );

  // Note: the "active page" visual state for product-to-product link buttons
  // (Individual / Set of 4) is handled server-side in snippets/button.liquid
  // so it survives section re-renders. This script only manages carrying the
  // selected variant across those links.

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyCarryoverFromUrl);
  } else {
    applyCarryoverFromUrl();
  }
})();
